import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import NexusHeader from '../components/NexusHeader';
import { Users, Plug, Cpu, CloudUpload, MessageCircle, Tag, Sliders, Shield, Activity } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Admin Control Panel Dashboard
 * Header matches Management Dashboard style exactly
 * White background, blue panels, white text, orange accents
 * Mobile-first (390×844), sticky header with safe area
 * Real-time metrics: AI Cost & Active Users
 */

const AdminDashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const t = useLang();
  const navigate = useNavigate();
  
  // Real-time metrics state
  const [aiUsage, setAiUsage] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Fetch metrics data
  const fetchMetrics = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    
    try {
            
      // Fetch AI Usage Report
      const usageResponse = await fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/report`, {
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        }
      });
      
      // Fetch Active Users
      const usersResponse = await fetch(`${BACKEND_URL}/api/admin-panel/active-users`, {
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        }
      });
      
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setAiUsage(usageData);
      }
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setActiveUsers(usersData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setLoading(false);
    } finally {
      if (isRefresh) {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  // Initial fetch and auto-refresh every 30 seconds
  useEffect(() => {
    fetchMetrics();
    const intervalId = setInterval(() => fetchMetrics(true), 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Format large numbers (e.g., 1420000 -> "1.42M")
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (!isAdmin()) {
    return null;
  }

  // Lucide React Icons - Minimalist & Consistent (EMZI CI)
  const icons = {
    groups: <Users size={28} strokeWidth={2} className="text-white" />,
    users: <Users size={28} strokeWidth={2} className="text-white" />,
    integrations: <Plug size={28} strokeWidth={2} className="text-white" />,
    ai: <Cpu size={28} strokeWidth={2} className="text-white" />,
    backup: <CloudUpload size={28} strokeWidth={2} className="text-white" />,
    whatsapp: <MessageCircle size={28} strokeWidth={2} className="text-white" />
  };

  return (
    <div 
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'white'
      }}
    >
      
      {/* Unified EMZI NEXUS Header */}
      <NexusHeader onLogout={logout} />

      {/* Scrollable Main Content */}
      <div 
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          padding: '12px 16px 100px'
        }}
      >
        
        {/* Admin Metrics Row - Two Cards (50/50) - Compact */}
        <div className="grid grid-cols-2 gap-2.5">
          {/* Card 1: AI Credits & Usage */}
          <Card 
            data-testid="ai-cost-card"
            className={`bg-white border-0 hover:shadow-lg transition-all duration-300 touch-target ${refreshing ? 'animate-pulse' : ''}`}
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '12px',
              borderBottom: refreshing ? '2px solid #FF9900' : 'none'
            }}
          >
            <CardContent className="p-3 text-center">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-16">
                  <div className="w-6 h-6 border-3 border-t-transparent border-emzi-blue rounded-full animate-spin mb-1"></div>
                  <div className="text-xs text-gray-400">Loading...</div>
                </div>
              ) : aiUsage ? (
                <>
                  <div className="text-2xl font-black text-emzi-blue mb-0.5">
                    {aiUsage.credits_used || (aiUsage.total_calls || 0) * 5} Credits
                  </div>
                  <div className="text-xs text-gray-600 font-semibold mb-1.5">
                    {aiUsage.total_calls || 0} calls &middot; {formatNumber(aiUsage.tokens_used || 0)} tokens
                  </div>
                  {/* Progress Bar */}
                  {aiUsage.limit && (
                    <div className="w-full bg-gray-200 rounded-full h-1 mb-0.5">
                      <div 
                        className="bg-emzi-blue h-1 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min((aiUsage.tokens_used / aiUsage.limit) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    AI Credits &amp; Usage
                  </div>
                </>
              ) : (
                <>
                  <div className="text-2xl font-black text-emzi-blue mb-0.5">
                    0 Credits
                  </div>
                  <div className="text-xs text-gray-600 font-semibold mb-1.5">
                    0 calls &middot; 0 tokens
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 mb-0.5">
                    <div className="bg-emzi-blue h-1 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    AI Credits &amp; Usage
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Card 2: Active Users */}
          <Card 
            data-testid="active-users-card"
            className={`bg-white border-0 hover:shadow-lg transition-all duration-300 touch-target ${refreshing ? 'animate-pulse' : ''}`}
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '12px',
              borderBottom: refreshing ? '2px solid #FF9900' : 'none'
            }}
          >
            <CardContent className="p-3 text-center">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-16">
                  <div className="w-6 h-6 border-3 border-t-transparent border-emzi-blue rounded-full animate-spin mb-1"></div>
                  <div className="text-xs text-gray-400">Loading...</div>
                </div>
              ) : activeUsers !== null ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-green-500 text-lg">●</span>
                    <div className="text-2xl font-black text-emzi-blue">
                      {activeUsers.active_users || 0}
                    </div>
                  </div>
                  {activeUsers.total_users > 0 && (
                    <div className="text-xs text-gray-400 mb-0.5">
                      / {activeUsers.total_users} {t('admin.metrics.totalLabel') || 'total'}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    {t('admin.metrics.activeUsers')}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-green-500 text-lg">●</span>
                    <div className="text-2xl font-black text-emzi-blue">
                      0
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('admin.metrics.activeUsers')}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* System Control Panel Card - Compact Blue Panel */}
        <Card 
          className="bg-emzi-blue border-0" 
          style={{
            boxShadow: '0 4px 16px rgba(0, 122, 255, 0.2)',
            borderRadius: '12px',
            marginTop: '8px'
          }}
        >
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-lg text-white flex items-center font-black">
              {t('admin.panel.systemControl')}
              <Badge className="ml-2 bg-emzi-orange text-white border-0 font-bold px-2 py-0.5 text-xs">8</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={() => navigate('/admin/functional-teams')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Users size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.cards.functionalTeams')}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/users')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Users size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.users.title')}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/integrations')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Plug size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.cards.integrations')}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/ai-model')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Cpu size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.cards.aiModel')}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/backup')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <CloudUpload size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.cards.backup')}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/whatsapp-templates')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <MessageCircle size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  WhatsApp Templates
                </span>
              </button>
              
              <button
                onClick={() => navigate('/admin/purpose-tags')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Tag size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  {t('admin.cards.purposeTags')}
                </span>
              </button>

              <button
                data-testid="ai-usage-by-user-btn"
                onClick={() => navigate('/admin/ai-usage')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Cpu size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  AI Usage by User
                </span>
              </button>

              <button
                data-testid="soa-settings-btn"
                onClick={() => navigate('/admin/soa-settings')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
              >
                <Sliders size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  SOA Settings
                </span>
              </button>

              <button
                data-testid="sso-settings-btn"
                onClick={() => navigate('/admin/sso-settings')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Shield size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  Nexus SSO
                </span>
              </button>

              <button
                data-testid="org-adoption-btn"
                onClick={() => navigate('/admin/org-adoption')}
                className="h-16 flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target"
                style={{
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  padding: '12px 8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Activity size={26} strokeWidth={2.5} color="white" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'white', textAlign: 'center', lineHeight: '1.2' }}>
                  Org Adoption
                </span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* System Version Footer */}
        <div className="text-center text-xs text-gray-500 py-2 pb-4">
          EMZI NEXUS V6.3 • System Control Panel
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
